import { useEffect, useMemo, useState } from 'react';
import { fetchQuery, graphql } from 'react-relay';

import { expValEquals } from '@confluence/feature-experiments';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import {
	experimentalPromotedSpacesQuery,
	type ExperimentalPromotedSpacesData,
} from '@confluence/legacy-schema-bridge/entry-points/experimentalPromotedSpacesQuery.ts';
import {
	useExperimentalPromotedSpacesQuery,
	type UseExperimentalPromotedSpacesQueryParams,
} from '@confluence/legacy-schema-bridge/entry-points/useExperimentalPromotedSpacesQuery.ts';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	defaultSpacesForCreateQuery as DefaultSpacesForCreateQuery,
	defaultSpacesForCreateQuery$data,
	defaultSpacesForCreateQuery$variables,
} from './__generated__/defaultSpacesForCreateQuery.graphql.ts';

// Reuse the same operation and fields for the create menu, selector, and template gallery.
const defaultSpacesForCreateQuery = graphql`
	query defaultSpacesForCreateQuery(
		$cloudId: ID!
		$promotedSpaceKeys: [String]
		$promotedSpacesLimit: Int
		$otherSpacesLimit: Int
	) {
		confluence_defaultSpaces(
			cloudId: $cloudId
			promotedSpaceKeys: $promotedSpaceKeys
			promotedSpacesLimit: $promotedSpacesLimit
			otherSpacesLimit: $otherSpacesLimit
		) {
			matchedPromotedSpaces {
				spaces {
					id
					key
					name
				}
			}
			promotedSpaces {
				spaces {
					id
					key
					name
				}
			}
			otherSpaces {
				spaces {
					id
					key
					name
				}
			}
		}
	}
`;

type PromotedSpacesVariables = NonNullable<UseExperimentalPromotedSpacesQueryParams['variables']>;
type DefaultSpaces = NonNullable<defaultSpacesForCreateQuery$data['confluence_defaultSpaces']>;
type DefaultSpace = DefaultSpaces['promotedSpaces']['spaces'][number];
type UseDefaultSpacesForCreateResult = {
	data?: ExperimentalPromotedSpacesData;
	loading: boolean;
	error?: Error | null;
};

const missingCloudIdError = new Error(
	'Cannot load default spaces from AGG without a Confluence cloud ID',
);
const unavailableDefaultSpacesError = new Error(
	'AGG default spaces are unavailable; check the confluence_default_spaces_graphql gate',
);

const toQueryVariables = (
	cloudId: string,
	variables?: PromotedSpacesVariables,
): defaultSpacesForCreateQuery$variables => ({
	cloudId,
	promotedSpaceKeys: variables?.promotedSpaceKey ? [variables.promotedSpaceKey] : [],
	promotedSpacesLimit: variables?.promotedSpacesLimit ?? 10,
	otherSpacesLimit: variables?.otherSpacesLimit ?? 10,
});

// Relay fetchQuery writes to the store, but network-only ensures no-cache callers receive fresh data.
const toRelayFetchPolicy = (fetchPolicy: UseExperimentalPromotedSpacesQueryParams['fetchPolicy']) =>
	fetchPolicy === undefined || fetchPolicy === 'cache-first' ? 'store-or-network' : 'network-only';

const toLegacySpace = ({ key, name }: DefaultSpace) => ({ id: key, text: name });

const toLegacyData = (spaces: DefaultSpaces): ExperimentalPromotedSpacesData => ({
	experimentalPromotedSpaces: {
		nodes: [...spaces.promotedSpaces.spaces, ...spaces.otherSpaces.spaces].map(toLegacySpace),
		matchedPromotedSpaces: spaces.matchedPromotedSpaces.spaces.map(toLegacySpace),
		__typename: 'ExperimentalPromotedSpaces',
	},
});

const useAggDefaultSpaces = ({
	cloudId,
	skip,
	variables,
	onError,
	fetchPolicy,
}: {
	cloudId: string;
	skip?: boolean;
	variables?: PromotedSpacesVariables;
	onError?: (error: Error) => void;
	fetchPolicy?: UseExperimentalPromotedSpacesQueryParams['fetchPolicy'];
}) => {
	const promotedSpaceKey = variables?.promotedSpaceKey;
	const promotedSpacesLimit = variables?.promotedSpacesLimit;
	const otherSpacesLimit = variables?.otherSpacesLimit;
	const queryVariables = useMemo(
		() => toQueryVariables(cloudId, { promotedSpaceKey, promotedSpacesLimit, otherSpacesLimit }),
		[cloudId, promotedSpaceKey, promotedSpacesLimit, otherSpacesLimit],
	);
	const relayFetchPolicy = toRelayFetchPolicy(fetchPolicy);
	const [result, setResult] = useState<{
		variables: defaultSpacesForCreateQuery$variables;
		fetchPolicy: ReturnType<typeof toRelayFetchPolicy>;
		data?: defaultSpacesForCreateQuery$data;
		error?: Error;
	}>();

	useEffect(() => {
		if (skip) {
			return;
		}

		const subscription = fetchQuery<DefaultSpacesForCreateQuery>(
			getRelayEnvironment(),
			defaultSpacesForCreateQuery,
			queryVariables,
			{ fetchPolicy: relayFetchPolicy },
		).subscribe({
			next: (data) => setResult({ variables: queryVariables, fetchPolicy: relayFetchPolicy, data }),
			error: (error: Error) => {
				setResult({ variables: queryVariables, fetchPolicy: relayFetchPolicy, error });
				onError?.(error);
			},
		});
		return () => subscription.unsubscribe();
	}, [onError, queryVariables, relayFetchPolicy, skip]);

	if (skip) {
		return { data: undefined, loading: false, error: undefined };
	}
	if (result?.variables !== queryVariables || result.fetchPolicy !== relayFetchPolicy) {
		return { data: undefined, loading: true, error: undefined };
	}
	return { data: result.data, loading: false, error: result.error };
};

const useDefaultSpacesForCreateWithExperiment = (
	isAggEnabled: boolean,
	{ skip, onError, variables, fetchPolicy }: UseExperimentalPromotedSpacesQueryParams,
): UseDefaultSpacesForCreateResult => {
	const { cloudId } = useSessionData();
	const aggResult = useAggDefaultSpaces({
		cloudId,
		skip: skip || !cloudId || !isAggEnabled,
		variables,
		onError,
		fetchPolicy,
	});
	const restResult = useExperimentalPromotedSpacesQuery({
		skip: skip || isAggEnabled,
		onError,
		variables,
		fetchPolicy,
	});

	if (!isAggEnabled) {
		return restResult;
	}

	return {
		data: aggResult.data?.confluence_defaultSpaces
			? toLegacyData(aggResult.data.confluence_defaultSpaces)
			: undefined,
		loading: aggResult.loading,
		error: skip
			? undefined
			: !cloudId
				? missingCloudIdError
				: (aggResult.error ??
					(aggResult.data?.confluence_defaultSpaces === null
						? unavailableDefaultSpacesError
						: undefined)),
	};
};

export const useDefaultSpacesForCreate = (
	params: UseExperimentalPromotedSpacesQueryParams = {},
): UseDefaultSpacesForCreateResult =>
	useDefaultSpacesForCreateWithExperiment(
		expValEquals('confluence_default_spaces_graphql', 'isEnabled', true),
		params,
	);

export const getDefaultSpacesForCreate = async ({
	variables,
	fetchPolicy,
}: {
	variables?: PromotedSpacesVariables;
	fetchPolicy?: 'cache-first' | 'network-only';
} = {}): Promise<{ data: ExperimentalPromotedSpacesData }> => {
	if (!expValEquals('confluence_default_spaces_graphql', 'isEnabled', true)) {
		return experimentalPromotedSpacesQuery({ variables, fetchPolicy });
	}
	const { cloudId } = await getSessionData();
	if (!cloudId) {
		throw missingCloudIdError;
	}

	const data = await fetchQuery<DefaultSpacesForCreateQuery>(
		getRelayEnvironment(),
		defaultSpacesForCreateQuery,
		toQueryVariables(cloudId, variables),
		{ fetchPolicy: fetchPolicy === 'network-only' ? 'network-only' : 'store-or-network' },
	).toPromise();
	if (!data?.confluence_defaultSpaces) {
		throw unavailableDefaultSpacesError;
	}

	return { data: toLegacyData(data.confluence_defaultSpaces) };
};
